import React from "react";
import { makeStyles } from "@mui/styles";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { RED_PRIMARY } from "./constants";

const useStyles = makeStyles({
  tutorialOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 10000,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    animation: "$fadeIn 0.3s ease-in",
  },
  tutorialContent: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "22px 24px 24px",
    maxWidth: "560px",
    width: "90%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
    position: "relative",
    animation: "$slideUp 0.3s ease-out",
  },
  tutorialTitle: {
    fontSize: "21.5px",
    fontWeight: "bold",
    marginBottom: "12px",
    textAlign: "center",
    color: "#1f2937",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  tutorialInstructions: {
    fontSize: "14.3px",
    lineHeight: 1.45,
    color: "#4b5563",
    marginBottom: "14px",
    fontFamily: "objektiv-mk2, sans-serif",
  },
  sectionHeading: {
    fontSize: "15.2px",
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: "8px",
    marginTop: "16px",
    fontFamily: "objektiv-mk2, sans-serif",
    "&:first-of-type": {
      marginTop: 0,
    },
  },
  sectionBlock: {
    marginBottom: "4px",
  },
  updateNoteShared: {
    marginTop: "14px",
    marginBottom: "6px",
    padding: "10px 12px",
    backgroundColor: "#fef2f2",
    borderRadius: "8px",
    fontSize: "13.4px",
    lineHeight: 1.42,
    color: "#374151",
    fontFamily: "objektiv-mk2, sans-serif",
    textAlign: "center",
  },
  updateNoteHighlight: {
    color: RED_PRIMARY,
    fontWeight: 700,
  },
  tutorialStep: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    marginBottom: "12px",
    padding: "10px",
    backgroundColor: "#f9fafb",
    borderRadius: "8px",
  },
  tutorialStepIcon: {
    flexShrink: 0,
    color: "#4e85af",
    marginTop: "2px",
    animation: "$pulse 2s ease-in-out infinite",
  },
  tutorialStepText: {
    flex: 1,
    fontSize: "13.4px",
    lineHeight: 1.42,
    color: "#374151",
  },
  tutorialButton: {
    width: "100%",
    padding: "10px 20px",
    backgroundColor: "#4e85af",
    color: "white",
    border: "none",
    borderRadius: "8px",
    fontSize: "14.3px",
    fontWeight: "bold",
    textTransform: "uppercase",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    fontFamily: "objektiv-mk2, sans-serif",
    "&:hover": {
      backgroundColor: "#41719a",
    },
  },
  "@keyframes fadeIn": {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  "@keyframes slideUp": {
    from: {
      transform: "translateY(20px)",
      opacity: 0,
    },
    to: {
      transform: "translateY(0)",
      opacity: 1,
    },
  },
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "scale(1)",
      opacity: 1,
    },
    "50%": {
      transform: "scale(1.1)",
      opacity: 0.8,
    },
  },
});

/**
 * Tutorial overlay that appears when reorder mode is activated
 * Shows instructions on how to drag and reorder table rows
 * The drag handles will pulse to draw attention
 */
function ReorderTutorial({ open, onClose }) {
  const classes = useStyles();

  if (!open) return null;

  return (
    <div className={classes.tutorialOverlay} onClick={onClose}>
        <div className={classes.tutorialContent} onClick={(e) => e.stopPropagation()}>
          <h2 className={classes.tutorialTitle}>How to Reorder Properties</h2>

          <h3 className={classes.sectionHeading}>1. Drag rows</h3>
          <div className={`${classes.tutorialInstructions} ${classes.sectionBlock}`}>
            <div className={classes.tutorialStep}>
              <DragIndicatorIcon className={classes.tutorialStepIcon} fontSize="small" />
              <div className={classes.tutorialStepText}>
                <strong>Click and hold</strong> the drag icon (⋮⋮) in the first column of any row
              </div>
            </div>

            <div className={classes.tutorialStep}>
              <ArrowDownwardIcon className={classes.tutorialStepIcon} fontSize="small" />
              <div className={classes.tutorialStepText}>
                <strong>Drag</strong> the row up or down to your desired position
              </div>
            </div>

            <div className={classes.tutorialStep}>
              <div
                className={classes.tutorialStepIcon}
                style={{
                  width: "18px",
                  height: "18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                ✓
              </div>
              <div className={classes.tutorialStepText}>
                <strong>Release</strong> to drop the row in its new position.
              </div>
            </div>
          </div>

          <h3 className={classes.sectionHeading}>2. Sort by PROPERTY TYPE or STATUS</h3>
          <div className={`${classes.tutorialInstructions} ${classes.sectionBlock}`}>
            <p style={{ margin: "0 0 7px 0", fontSize: "14.3px", lineHeight: 1.45 }}>
              Use <strong>Sort by STATUS</strong> or <strong>Sort by PROPERTY TYPE</strong> above the table to
              sort alphabetically. Click the same button again to switch between ascending and descending order.
              You can combine sorting with dragging to get the order you want.
            </p>
          </div>

          <div className={classes.updateNoteShared}>
            <span className={classes.updateNoteHighlight}>
              The order will be saved when you click the UPDATE button.
            </span>{" "}
            <br/>
            That applies whether you dragged rows or used the sort buttons.
          </div>

          <button className={classes.tutorialButton} onClick={onClose}>
            Got it, let's reorder!
          </button>
        </div>
      </div>
  );
}

export default ReorderTutorial;
